import clsx from "clsx";
import { type ReactElement, type ReactNode } from "react";
import { DropIndicator, GridList, type GridListProps, useDragAndDrop } from "react-aria-components";

import style from "./ReorderableList.module.css";

export interface DraggableItemData {
  id: string;
  name: string;
}

export type DraggableItem<T = unknown> = DraggableItemData & T;

interface ReorderableListProps<T> extends Omit<GridListProps<DraggableItem<T>>, "children" | "dragAndDropHooks"> {
  items: DraggableItem<T>[];
  onReorder: (items: DraggableItem<T>[]) => void;
  renderDragPreview?: (item: DraggableItem<T>) => ReactElement;
  children: (item: DraggableItem<T>) => ReactNode;
  dropIndicatorClassName?: string;
}

export function ReorderableList<T extends DraggableItemData>({
  items,
  onReorder,
  renderDragPreview,
  children,
  selectionMode = "none",
  selectionBehavior = "replace",
  ...props
}: ReorderableListProps<T>) {
  const { dragAndDropHooks } = useDragAndDrop({
    getItems: (keys) => {
      return [...keys].map((key) => {
        const item = items.find((item) => item.id === key);
        return {
          "text/plain": item?.name ?? item?.id ?? "",
          "item-data": JSON.stringify(item),
        };
      });
    },
    onReorder(e) {
      const draggedKeys = Array.from(e.keys).map(String);
      const targetKey = String(e.target.key);

      // Get items being dragged
      const draggedItems = items.filter((item) => draggedKeys.includes(item.id));

      // Remove dragged items from the list
      const newItems = items.filter((item) => !draggedKeys.includes(item.id));

      // Find target index in the new list
      const targetIndex = newItems.findIndex((item) => item.id === targetKey);

      if (targetIndex === -1) {
        // Target was one of the dragged items, don't reorder
        return;
      }

      // Calculate insertion index based on drop position
      let insertIndex;
      if (e.target.dropPosition === "before") {
        insertIndex = targetIndex;
      } else {
        insertIndex = targetIndex + 1;
      }

      // Insert dragged items at the new position
      newItems.splice(insertIndex, 0, ...draggedItems);

      onReorder(newItems);
    },
    getDropOperation(target, types, allowedOperations) {
      // For reordering, we want to return 'move' operation
      if (types.has("text/plain") && target) {
        return "move";
      }

      // Default fallback
      return allowedOperations[0] || "cancel";
    },
    renderDragPreview: (items) => {
      // If custom preview renderer is provided
      if (renderDragPreview) {
        // Extract the item data
        const itemDataString = items[0]["item-data"];

        if (!itemDataString) {
          return <div>{items[0]["text/plain"]}</div>;
        }

        try {
          const itemData = JSON.parse(itemDataString);
          return renderDragPreview(itemData);
        } catch {
          return <div>{items[0]["text/plain"]}</div>;
        }
      }

      // Default fallback preview if no custom renderer provided
      return <div>{items[0]["text/plain"]}</div>;
    },
    renderDropIndicator(target) {
      return (
        <DropIndicator
          target={target}
          className={({ isDropTarget }) =>
            clsx(props.dropIndicatorClassName, {
              [style.dropIndicator]: isDropTarget,
            })
          }
        />
      );
    },
  });

  return (
    <GridList
      aria-label={"Reorderable List"}
      {...props}
      items={items}
      dragAndDropHooks={dragAndDropHooks}
      selectionMode={selectionMode}
      selectionBehavior={selectionBehavior}
    >
      {(item) => children(item)}
    </GridList>
  );
}
